import { Button } from "../ui/button";
import { Sparkles } from "lucide-react";
import { useLocation } from "wouter";

const Hero = () => {
  const [_, setLocation] = useLocation();

  return (
    <section className="relative overflow-hidden bg-white py-16 transition-colors duration-200 dark:bg-gray-900 md:py-24">
      {/* Modern background pattern */}
      <div className="absolute inset-0 z-0 opacity-5 dark:opacity-10">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/30 blur-3xl dark:bg-primary/50"></div>
        <div className="absolute top-1/2 right-0 h-64 w-64 rounded-full bg-blue-500/30 blur-3xl dark:bg-blue-500/50"></div>
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl dark:bg-purple-500/40"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:flex lg:items-center lg:gap-16">
          <div className="mb-10 lg:mb-0 lg:w-1/2">
            <div className="mb-4 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary dark:bg-primary-dark/20 dark:text-primary-dark">
              <Sparkles className="mr-1 h-4 w-4" />
              <span>Premium Dining Experience</span>
            </div>
            <h1 className="mb-6 font-serif text-4xl font-bold leading-tight text-gray-800 dark:text-white sm:text-5xl md:text-6xl">
              Discover Your Next{" "}
              <span className="bg-gradient-to-r from-primary to-rose-600 bg-clip-text text-transparent dark:from-primary-dark dark:to-rose-400">
                Culinary Delight
              </span>
            </h1>
            <p className="mb-8 text-lg text-gray-600 dark:text-gray-300 md:text-xl">
              Experience extraordinary dining at premium restaurants for just
              €15. Let us surprise you with a curated gourmet experience that exceeds expectations.
            </p>
            <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
              <Button
                onClick={() => { setLocation('/explore'); }}
                size="lg"
                className="bg-primary px-8 py-6 text-lg font-medium text-white transition-all hover:bg-primary/90 hover:shadow-lg"
              >
                <div className="flex items-center justify-center">
                  <span>Find Surprises</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </Button>
              <Button
                onClick={() => { setLocation('/how-it-works'); }}
                variant="outline"
                size="lg"
                className="border-gray-300 px-8 py-6 text-lg font-medium text-gray-700 transition-all hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <div className="flex items-center justify-center">
                  <span>How It Works</span>
                </div>
              </Button>
            </div>
          </div>
          <div className="relative lg:w-1/2">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1559329007-40df8a9345d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                alt="Modern restaurant with gourmet food"
                className="h-auto w-full rounded-2xl object-cover transition-transform duration-700 hover:scale-105"
                style={{ height: "500px" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="mb-2 flex items-center space-x-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="font-medium">4.9 (500+ ratings)</span>
                </div>
                <p className="text-base font-medium">
                  "The surprise dining experience exceeded all my expectations!"
                </p>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-6 hidden max-w-xs rounded-xl bg-white p-4 shadow-xl transition-transform duration-300 hover:-translate-y-1 dark:bg-gray-800 sm:block">
              <div className="flex items-start space-x-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7 text-primary dark:text-primary-dark"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 dark:text-white">
                    5,000+ Surprises
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Delivered to adventurous food enthusiasts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
